import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { ArrowRight, CheckSquare } from "lucide-react";

const CSATCurriculum = ({ isPro, onBack }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [curriculum, setCurriculum] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayContent, setDayContent] = useState(null);

  // Fetch curriculum when component mounts
  useEffect(() => {
    const fetchCurriculum = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/csat/curriculum`);
        setCurriculum(data.curriculum || []);
      } catch (err) {
        toast({ variant: "destructive", description: "Could not load curriculum." });
      } finally {
        setLoading(false);
      }
    };
    
    fetchCurriculum();
  }, [toast]);

  // Fetch specific day content
  const fetchDayContent = async (dayNumber) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/csat/curriculum/day/${dayNumber}`);
      setDayContent(data);
    } catch (err) {
      toast({ variant: "destructive", description: err.response?.data?.detail || "Could not load day content." });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setDayContent(null);
    setSelectedDay(null);
    onBack();
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-center justify-between">
          <Button variant="outline" className="border-slate-200" onClick={handleBack}>
            ← Back to CSAT Suite
          </Button>
          <Badge variant="outline" className={`px-4 py-2 text-sm ${isPro ? 'border-slate-900 text-slate-900' : 'border-slate-300 text-slate-500'}`}>
            {isPro ? "PRO" : "FREE"}
          </Badge>
        </div>

        {/* Header - CLARIAS Style */}
        <div className="pb-6 border-b border-slate-100">
          <h1 className="text-2xl font-semibold text-slate-900">30-Day CSAT Curriculum</h1>
          <p className="text-slate-500">
            Structured learning path from basics to mastery
          </p>
        </div>

        {/* If viewing specific day */}
        {dayContent ? (
          <div className="space-y-6">
            <Button variant="outline" onClick={() => setDayContent(null)}>
              ← Back to Curriculum
            </Button>

            <Card className="border-indigo-200 bg-indigo-50">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-indigo-900 mb-2">
                  Day {dayContent.day}: {dayContent.title}
                </h2>
                <p className="text-sm text-indigo-700">{dayContent.focus}</p>
              </CardContent>
            </Card>

            {/* Topics Covered */}
            {dayContent.topics && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">📚 Topics Covered</h3>
                  <ul className="space-y-2">
                    {dayContent.topics.map((topic, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckSquare className="w-4 h-4 text-green-600 mt-0.5" />
                        <span className="text-sm text-slate-700">{topic}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Practice Questions */}
            {dayContent.practice_count && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">✏️ Practice</h3>
                  <p className="text-sm text-slate-700">
                    Solve {dayContent.practice_count} questions to reinforce today&apos;s concepts
                  </p>
                  <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700">
                    Start Practice
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Learning Objective */}
            {dayContent.objective && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Learning Objective</h3>
                  <p className="text-sm text-slate-700">{dayContent.objective}</p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          // Show curriculum overview
          <div className="space-y-4">
            {loading ? (
              <p className="text-center text-slate-600">Loading curriculum...</p>
            ) : curriculum.length === 0 ? (
              <p className="text-center text-slate-600">No curriculum available</p>
            ) : (
              <>
                {curriculum.map((day) => (
                  <Card 
                    key={day.day} 
                    className="cursor-pointer hover:shadow-lg transition-all border-indigo-200"
                    onClick={() => {
                      setSelectedDay(day.day);
                      fetchDayContent(day.day);
                    }}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-start gap-4">
                          <div className="bg-indigo-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold">
                            {day.day}
                          </div>
                          <div>
                            <h3 className="font-bold text-slate-900 mb-1">{day.title}</h3>
                            <p className="text-sm text-slate-600">{day.focus}</p>
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-indigo-600" />
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Paywall for FREE users */}
                {!isPro && curriculum.length < 30 && (
                  <Card className="border-orange-200 bg-orange-50">
                    <CardContent className="p-6 text-center">
                      <p className="text-sm text-orange-800 mb-4">
                        🔒 Days 8-30 available only for PRO users. Upgrade to unlock the complete curriculum!
                      </p>
                      <Button className="bg-orange-600 hover:bg-orange-700">
                        Upgrade to PRO
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CSATCurriculum;
